import { useIDBKeyval } from '@vueuse/integrations/useIDBKeyval'
import { acceptHMRUpdate, defineStore } from 'pinia'

interface PolicyIssue {
  policy: {
    id: string
    name: string
  }
  message: string
  url: string
}

interface ContentValidationState {
  initializing: boolean
  initialized: boolean
  errors: PolicyIssue[]
  warnings: PolicyIssue[]
}

export const useContentValidation = defineStore('content-validation', {
  state: () => {
    const { data: errors } = useIDBKeyval<PolicyIssue[]>('integrity-errors', [])
    const { data: warnings } = useIDBKeyval<PolicyIssue[]>(
      'integrity-warnings',
      [],
    )
    return {
      initializing: false,
      initialized: false,
      errors,
      warnings,
    }
  },

  getters: {
    errorCount: (state) => state.errors.length,
    errorsByPolicy: (state) => (policyId: string) =>
      state.errors.filter((error: PolicyIssue) => error.policy.id === policyId),
    warningCount: (state) => state.warnings.length,
    warningsByPolicy: (state) => (policyId: string) =>
      state.warnings.filter(
        (warning: PolicyIssue) => warning.policy.id === policyId,
      ),
  },

  actions: {
    async fetchHealth() {
      const apiClient = useApiClient({ baseURL: '/api' })

      const health = await apiClient.fetch<ContentValidationState>('/integrity')

      this.$patch({
        ...health,
        initialized: true,
        initializing: false,
      })

      return health
    },

    // stub to fix type error
    loadHealth() {},

    init() {
      if (this.initializing || this.initialized) {
        return
      }
      this.initializing = true

      return this.fetchHealth()
    },
  },
})

if (import.meta.hot)
  import.meta.hot.accept(acceptHMRUpdate(useContentValidation, import.meta.hot))
